import { Button } from './ui/button';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Hero = () => {
  const [ref, isVisible] = useScrollAnimation();

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="min-h-screen pt-20 flex items-center bg-secondary relative overflow-hidden"
    >
      {/* Subtle texture */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1656331797721-b593b8f00297?w=1920&q=10')`,
            backgroundSize: 'cover',
          }}
        />
      </div>

      <div className="container-width relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div
            ref={ref}
            className={`animate-on-scroll ${isVisible ? 'visible' : ''}`}
          >
            <span className="inline-block text-sm font-medium tracking-widest uppercase text-primary/60 mb-6">
              Healthcare Staffing Excellence
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-foreground mb-6">
              Healthcare Staffing that Delivers{' '}
              <span className="text-primary">Quality</span>,{' '}
              <span className="text-primary">Compassion</span> &{' '}
              <span className="text-primary">Reliability</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-muted-foreground mb-10 max-w-xl">
              We specialize in connecting highly qualified nursing, allied health,
              and non-clinical professionals with healthcare facilities in need of
              skilled, reliable talent. Our goal is to ensure the right people are
              placed in the right roles—helping organizations deliver exceptional
              care and maintain operational excellence.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/find-talent">
                <Button
                  className="bg-primary hover:bg-primary/90 text-white btn-hover-lift rounded-full px-8 py-6 text-base"
                  data-testid="hire-talent-btn"
                >
                  Hire Talent
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/careers">
                <Button
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary hover:text-white btn-hover-lift rounded-full px-8 py-6 text-base"
                  data-testid="browse-jobs-btn"
                >
                  Browse Jobs
                </Button>
              </Link>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative lg:h-[600px] hidden lg:block">
            <div className="absolute inset-0 hero-image-mask rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/3c79fc15-7acb-4945-a01c-dd7a9cf21362/images/dade111af330bfa8eb26a49bacf6906a245000920e9f0a33f418046594187ed0.png"
                alt="Diverse team of healthcare professionals including nurses and allied health workers"
                className="w-full h-full object-cover object-top"
                data-testid="hero-image"
              />
            </div>
            {/* Decorative elements */}
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-primary/20 rounded-full blur-2xl" />
            <div className="absolute -top-6 -right-6 w-40 h-40 bg-accent/10 rounded-full blur-3xl" />
          </div>
        </div>
      </div>

      {/* Scroll indicator - hidden on mobile to avoid overlap with buttons */}
      <button
        onClick={() => scrollToSection('#staffing')}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-muted-foreground hover:text-primary transition-colors group z-20 hidden md:flex"
        data-testid="scroll-indicator"
      >
        <span className="text-xs font-medium uppercase tracking-widest">Scroll</span>
        <ChevronDown className="h-5 w-5 group-hover:translate-y-1 transition-transform" />
      </button>
    </section>
  );
};

export default Hero;
